import React, { useContext, useEffect, useState, useRef } from "react";
import "../account.css";
import "./accountNotifications.css";
import translations from "../../../translations/translations";
import { CombinedContext } from "../../../context/combinedContext";
import usersRequest from "../../../request/usersRequest";
import AccountNotificationsSkeleton from "./accountNotificationsSkeleton/accountNotificationsSkeleton";
import { Bell, CalendarCheck, CalendarClock, Star, Store, UserCog } from "lucide-react";

const SECTION_ICONS = {
    bookings: CalendarCheck,
    reminders: CalendarClock,
    reviews: Star,
    shop: Store,
    staff: UserCog,
};

const AccountNotifications = () => {
    const { language, userData } = useContext(CombinedContext);
    const role = userData?.user?.role;
    const accessToken = userData?.cognitoTokens?.AccessToken;

    const [notificationPrefs, setNotificationPrefs] = useState(null);
    const [loading, setLoading] = useState(true);

    const saveTimeoutRef = useRef(null);
    const pendingUpdatesRef = useRef({});

    /* ----------------------------
       LOAD PREFS
    ----------------------------- */
    useEffect(() => {
        if (!accessToken) return;

        const loadPrefs = async () => {
            try {
                const resp = await usersRequest.getNotificationPreferences(accessToken);
                setNotificationPrefs(resp.notificationPrefs || {});
            } catch (err) {
                console.error("Failed to load notification prefs", err);
            } finally {
                setLoading(false);
            }
        };

        loadPrefs();
    }, [accessToken]);

    /* ----------------------------
       SAVE PREFS (DEBOUNCED)
    ----------------------------- */
    const savePrefs = (updates) => {
        // merge updates instead of replacing
        pendingUpdatesRef.current = {
            ...pendingUpdatesRef.current,
            ...updates,
        };

        if (saveTimeoutRef.current) return; // already scheduled

        saveTimeoutRef.current = setTimeout(async () => {
            const updatesToSend = pendingUpdatesRef.current;

            // reset BEFORE request to avoid race issues
            pendingUpdatesRef.current = {};
            saveTimeoutRef.current = null;

            try {
                await usersRequest.updateNotificationPreferences(updatesToSend, accessToken);
            } catch (err) {
                console.error("Failed to update notification prefs", err);
            }
        }, 400);
    };

    /* ----------------------------
       TOGGLE
    ----------------------------- */
    const toggleChannel = (key, channel) => {
        setNotificationPrefs((prev) => {
            const next = {
                ...prev,
                [key]: {
                    ...prev[key],
                    [channel]: !prev[key]?.[channel],
                },
            };

            savePrefs({ [key]: next[key] });
            return next;
        });
    };

    if (!role || loading || !notificationPrefs) {
        return <AccountNotificationsSkeleton />;
    }

    const getSection = (key) => {
        if (key.toLowerCase().includes("reminder")) return "reminders";
        if (key.toLowerCase().includes("review")) return "reviews";
        if (key.toLowerCase().includes("staff") || key.toLowerCase().includes("role")) return "staff";
        if (key.toLowerCase().includes("shop")) return "shop";
        return "bookings";
    };

    const getSectionLabel = (section) =>
        translations[language]?.account?.notifications?.sections?.[section.toLowerCase()] || section;

    const getItemLabel = (key) => translations[language]?.account?.notifications?.items?.[key] || key;

    const sections = Object.keys(notificationPrefs).reduce((acc, key) => {
        const section = getSection(key);
        acc[section] = acc[section] || [];
        acc[section].push(key);
        return acc;
    }, {});

    const t = translations[language].account.notifications;

    return (
        <div className="ac-page apn">
            <header className="ac-head">
                {/* <span className="ac-eyebrow">{translations[language].account.menu?.notifications || "Notifications"}</span> */}
                <h1 className="ac-title">{t.title}</h1>
                {t.subtitle && <p className="ac-sub">{t.subtitle}</p>}
            </header>

            {Object.entries(sections).map(([sectionKey, keys]) => {
                const SectionIcon = SECTION_ICONS[sectionKey] || Bell;

                return (
                    <section key={sectionKey} className="ac-card">
                        <div className="ac-card-head">
                            <span className="ac-icon">
                                <SectionIcon size={16} />
                            </span>
                            <h3>{getSectionLabel(sectionKey)}</h3>
                        </div>

                        <div className="ac-card-body is-flush">
                            {keys.map((key) => (
                                <div key={key} className="apn-item">
                                    <p className="apn-item-title">{getItemLabel(key)}</p>

                                    <div className="apn-channels">
                                        <button
                                            type="button"
                                            className={`apn-channel ${notificationPrefs[key]?.email ? "is-on" : ""}`}
                                            onClick={() => toggleChannel(key, "email")}
                                        >
                                            <span>{t.emailLabel}</span>
                                            <span className={`ac-toggle ${notificationPrefs[key]?.email ? "is-on" : ""}`} />
                                        </button>

                                        <button
                                            type="button"
                                            className={`apn-channel ${notificationPrefs[key]?.push ? "is-on" : ""}`}
                                            onClick={() => toggleChannel(key, "push")}
                                        >
                                            <span>{t.pushLabel}</span>
                                            <span className={`ac-toggle ${notificationPrefs[key]?.push ? "is-on" : ""}`} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default AccountNotifications;
